/**
 * DateField component.
 *
 * Date input using native HTML date picker.
 * Validates that a valid date has been selected.
 */

import type { FormFieldDefinition } from "../../types";
import { isFutureDate, todayLocalISO } from "../../utils/dateBounds";

export interface DateFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class DateField {
  private readonly element: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly errorEl: HTMLElement;

  constructor(private readonly options: DateFieldOptions) {
    const { field } = options;

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = field.label;
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // Date input
    this.inputEl = document.createElement("input");
    this.inputEl.type = "date";
    this.inputEl.id = `hana-field-${field.name}`;
    this.inputEl.className =
      "hana-form-field__input hana-form-field__input--date";
    this.inputEl.name = field.name;
    this.inputEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.inputEl.setAttribute("aria-required", "true");
    }
    // Date of birth can never be in the future, and DOB is the only `date`
    // field the backend emits, so cap the native picker at today. Submit-time
    // validation in InlineForm catches typed values that bypass the picker.
    // The backend's DOB check is authoritative.
    this.inputEl.max = todayLocalISO();
    if (field.value) {
      this.inputEl.value = field.value;
    }

    this.inputEl.addEventListener("change", () => {
      this.clearError();
      // The native `max` attribute caps the calendar picker but does NOT
      // stop a future date typed into the date segments. Reject it the
      // same way the picker does: clear the value + surface the error
      // immediately, so a future date of birth can never be entered.
      if (isFutureDate(this.inputEl.value)) {
        this.inputEl.value = "";
        this.setError("Date of birth cannot be in the future");
        this.options.onChange(field.name, "");
        return;
      }
      this.options.onChange(field.name, this.inputEl.value);
    });

    this.element.appendChild(this.inputEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.inputEl.value;
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.inputEl.classList.add("hana-form-field__input--error");
    this.inputEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.inputEl.classList.remove("hana-form-field__input--error");
    this.inputEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.inputEl.disabled = disabled;
  }

  focus(): void {
    this.inputEl.focus();
  }
}
